<script setup lang="ts">
import { LMap, LTileLayer, LIcon, LPopup, LMarker, LControl } from '@vue-leaflet/vue-leaflet'

const assignedLocations = inject<Ref<ILocationModel[]>>('assignedLocations')!
const cmsLocation = inject<Ref<ICMSLocationModel>>('cmsLocation')!

const props = defineProps<{
	scroll: boolean
}>()

const locationMap = ref<any>(null)
const { isMobile_lg } = useWindowWidth()
const userLocationMarker = ref(null)
const L = ref<any>(null)
const notyf = useNotyfService()
const { locale } = useI18n()
const $t = useI18n().t

const initLeaflet = async () => {
	if (!L.value) {
		const leaflet = await import('leaflet')
		L.value = leaflet.default
	}
	return L.value
}

watch(
	() => props.scroll,
	() => {
		if (props.scroll === true) {
			locationMap.value?.leafletObject.scrollWheelZoom.enable()
		}
	},
)

watch(isMobile_lg, () => {
	if (isMobile_lg.value) {
		locationMap.value?.leafletObject.dragging.disable()
		locationMap.value?.leafletObject.tapHold.disable()
	} else {
		locationMap.value?.leafletObject.dragging.enable()
		locationMap.value?.leafletObject.tapHold.enable()
	}
})

const onMapReady = async () => {
	await initLeaflet()
	setTimeout(() => {
		if (locationMap.value?.leafletObject) {
			locationMap.value.leafletObject.invalidateSize()
			if (isMobile_lg.value) {
				locationMap.value.leafletObject.dragging.disable()
				locationMap.value.leafletObject.tapHold.disable()
			} else {
				locationMap.value.leafletObject.dragging.enable()
				locationMap.value.leafletObject.tapHold.enable()
			}
		}
	}, 100)
}

const mapOptions = computed(() => ({
	zoomControl: true,
	scrollWheelZoom: false,
	fullscreenControl: true,
}))

const maximizeMap = () => {
	try {
		if (locationMap.value?.leafletObject) {
			if (document.fullscreenElement) {
				document.exitFullscreen()
			} else {
				locationMap.value.leafletObject.getContainer().requestFullscreen()
			}
		}
	} catch (error) {
		notyf?.error((error as Error).message)
	}
}

const getCurrentLocation = async () => {
	try {
		const L = await initLeaflet()

		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords
					locationMap.value?.leafletObject.setView([latitude, longitude], 13)

					// Add a marker for the user's location
					const userIcon = L.icon({
						iconUrl:
							"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='512' viewBox='0 0 320 512'%3E%3Cpath fill='%23ff5c00' d='M112 48a48 48 0 1 1 96 0a48 48 0 1 1-96 0m40 304v128c0 17.7-14.3 32-32 32s-32-14.3-32-32V256.9l-28.6 47.6c-9.1 15.1-28.8 20-43.9 10.9s-20-28.8-10.9-43.9l58.3-97c17.4-28.9 48.6-46.6 82.3-46.6h29.7c33.7 0 64.9 17.7 82.3 46.6l58.3 97c9.1 15.1 4.2 34.8-10.9 43.9s-34.8 4.2-43.9-10.9L232 256.9V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V352z'/%3E%3C/svg%3E",
						iconSize: [25, 41],
						iconAnchor: [12, 41],
						popupAnchor: [1, -34],
					})

					if (userLocationMarker.value) {
						locationMap.value?.leafletObject.removeLayer(userLocationMarker.value)
					}

					userLocationMarker.value = L.marker([latitude, longitude], { icon: userIcon }).addTo(locationMap.value.leafletObject)
				},
				(error) => {
					console.error('Error getting location:', error.message)
					notyf?.error($t('error.unable_to_retrieve'))
				},
			)
		} else {
			notyf?.error($t('error.geolocation_is_not_supported'))
		}
	} catch (error) {
		console.error('Error initializing Leaflet:', error)
	}
}

onMounted(async () => {
	await initLeaflet()

	if (locationMap.value?.leafletObject) {
		locationMap.value.leafletObject.on('fullscreenchange', () => {
			locationMap.value?.leafletObject.invalidateSize()
		})
	}
})

const formatPhoneNumber = (phoneNumber: string) => {
	if (!phoneNumber || phoneNumber?.length < 9) return phoneNumber

	const phone = phoneNumber.replace('+', '')
	return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8, 11)}`.trim()
}

const mapCenter = computed(() => {
	if (assignedLocations.value && assignedLocations.value?.length > 0) {
		return [assignedLocations.value[0].geo_point.coordinates[1], assignedLocations.value[0].geo_point.coordinates[0]]
	} else {
		return [cmsLocation.value?.geoPoint.coordinates[1], cmsLocation.value?.geoPoint.coordinates[0]]
	}
})
</script>

<template>
	<div class="location-map">
		<ClientOnly>
			<LMap
				ref="locationMap"
				:zoom="isMobile_lg ? 12 : 14"
				:center="mapCenter"
				:use-global-leaflet="false"
				:options="mapOptions"
				@ready="onMapReady">
				<LTileLayer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					attribution='&amp;copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
					layer-type="base"
					name="OpenStreetMap" />
				<template v-if="assignedLocations && assignedLocations?.length > 0">
					<LMarker
						v-for="location in assignedLocations"
						:key="location.id"
						:lat-lng="[location.geo_point.coordinates[1], location.geo_point.coordinates[0]]">
						<LIcon :icon-size="[20, 20]">
							<GlobalIcon class="location-map__icon" name="location-bold" :width="20" />
						</LIcon>
						<LPopup :options="{ direction: 'top', offset: [0, -20], closeButton: false }">
							<div class="location-map__tooltip">
								<GlobalIcon v-if="location.airport" class="location-map__icon" name="airplane" :width="20" />
								<GlobalIcon v-if="!location.airport" class="location-map__icon" name="location-bold" :width="20" />
								<div class="location-map__tooltip-text-container">
									<p class="location-map__tooltip-text location-map__tooltip-text--title">
										{{ location.name }}
									</p>
									<p
										v-if="!location.address_street?.includes('null') && !location.address_house_number?.includes('null')"
										class="location-map__tooltip-text location-map__tooltip-text--street">
										{{ location.address_street }} {{ location.address_house_number }}
									</p>
									<div class="location-map__tooltip-text location-map__tooltip-text--number">
										<GlobalIcon name="call" :height="14" class="header__call-btn__icon" />
										<p>
											{{
												!location.phone_number?.includes('null') && location?.phone_number
													? formatPhoneNumber(location.phone_number)
													: '+48 794 500 550'
											}}
										</p>
									</div>
								</div>
							</div>
						</LPopup>
					</LMarker>
				</template>
				<LMarker v-else :lat-lng="mapCenter">
					<LIcon :icon-size="[20, 20]">
						<GlobalIcon class="location-map__icon" name="location-bold" :width="20" />
					</LIcon>
					<LPopup :options="{ direction: 'top', offset: [0, -20], closeButton: false }">
						<div class="location-map__tooltip">
							<GlobalIcon class="location-map__icon" name="location-bold" :width="20" />
							<div class="location-map__tooltip-text-container">
								<p
									v-if="cmsLocation[`name${locale?.toUpperCase()}`]"
									class="location-map__tooltip-text location-map__tooltip-text--title">
									{{ cmsLocation[`name${locale?.toUpperCase()}`] }}
								</p>
								<p
									v-if="cmsLocation[`address${locale?.toUpperCase()}`]"
									class="location-map__tooltip-text location-map__tooltip-text--street">
									{{ cmsLocation[`address${locale?.toUpperCase()}`] }}
								</p>
								<div class="location-map__tooltip-text location-map__tooltip-text--number">
									<GlobalIcon name="call" :height="14" class="header__call-btn__icon" />
									<p>
										{{
											cmsLocation.phoneNumber && !cmsLocation.phoneNumber?.includes('null')
												? formatPhoneNumber(cmsLocation.phoneNumber)
												: '+48 794 500 550'
										}}
									</p>
								</div>
							</div>
						</div>
					</LPopup>
				</LMarker>
				<LControl position="bottomright">
					<div class="location-map__btn-container">
						<button class="location-map__btn location-map__btn--maximize" @click="maximizeMap">
							<GlobalIcon name="maximize" :height="20" />
						</button>
						<button class="location-map__btn location-map__btn--gps" aria-label="Get current location" @click="getCurrentLocation">
							<GlobalIcon name="gps" :height="20" />
						</button>
					</div>
				</LControl>
			</LMap>
		</ClientOnly>
	</div>
</template>

<style scoped lang="scss">
@import './Map.scss';
</style>
